import { useRoute } from 'vue-router'

type PageKey =
  | 'home'
  | 'about'
  | 'music'
  | 'events'
  | 'contact'
  | 'news'
  | 'school'
  | 'legal'
  | 'privacy'
  | 'cookies'

type Breadcrumb = { name: string; url: string }

type PageMeta = {
  title: string
  description: string
  path: string
  type?: string
  ogImage?: string
  lang?: string
  breadcrumbs?: Breadcrumb[]
}

const siteUrl = 'https://bandadenoia.gal'
const siteName = 'Banda de Música de Noia'
const defaultLang = 'gl-ES'
const defaultOgImage = absoluteUrl('/og-image.jpg')

const pageMetaMap: Record<PageKey, PageMeta> = {
  home: {
    title: 'Banda de Música de Noia | Banda tradicional galega dende 1890',
    description:
      'Banda de Música de Noia: concertos, procesións e eventos en Galicia. Escoita a nosa música, consulta o calendario e contacta para actuacións.',
    path: '/',
    type: 'website',
    breadcrumbs: [{ name: 'Inicio', url: '/' }],
  },
  about: {
    title: 'Historia da Banda de Música de Noia',
    description:
      'Coñece a traxectoria da Banda de Música de Noia dende 1890: logros, viaxes internacionais e membros destacados.',
    path: '/about',
  },
  music: {
    title: 'Música e vídeos da Banda de Noia',
    description:
      'Escoita e mira actuacións da Banda de Música de Noia: vídeos destacados e concertos memorables en Galicia e no exterior.',
    path: '/music',
    type: 'video.other',
  },
  events: {
    title: 'Calendario de actuacións | Banda de Noia',
    description:
      'Consulta as próximas actuacións da Banda de Música de Noia: concertos, procesións e eventos especiais en Galicia.',
    path: '/events',
    type: 'event',
  },
  contact: {
    title: 'Contacto | Banda de Música de Noia',
    description:
      'Contacta coa Banda de Música de Noia para contratacións, concertos e colaboracións. Resposta rápida e profesional.',
    path: '/contact',
  },
  news: {
    title: 'Novas | Banda de Música de Noia',
    description:
      'Actualidade e novidades da Banda de Música de Noia: anuncios, concertos e notas de prensa.',
    path: '/news',
    type: 'article',
  },
  school: {
    title: 'Escola de Música | Banda de Noia',
    description:
      'Escola de Música da Banda de Noia: docencia, canteira e integración na banda. Información para alumnado e familias.',
    path: '/school',
  },
  legal: {
    title: 'Aviso legal | Banda de Música de Noia',
    description: 'Información legal da Banda de Música de Noia.',
    path: '/legal',
  },
  privacy: {
    title: 'Política de privacidade | Banda de Música de Noia',
    description:
      'Consulta a política de privacidade da Banda de Música de Noia e o tratamento de datos persoais.',
    path: '/privacy',
  },
  cookies: {
    title: 'Política de cookies | Banda de Música de Noia',
    description:
      'Información sobre o uso de cookies na web da Banda de Música de Noia.',
    path: '/cookies',
  },
}

export function absoluteUrl(pathOrUrl: string) {
  if (!pathOrUrl) return siteUrl
  if (pathOrUrl.startsWith('http')) return pathOrUrl
  return new URL(pathOrUrl, siteUrl).toString()
}

export function getPageMeta(page: PageKey): PageMeta {
  return pageMetaMap[page]
}

export function buildHeadConfig(
  page: PageKey,
  options?: {
    title?: string
    description?: string
    jsonLd?: object[]
  },
) {
  const base = getPageMeta(page)
  const title = options?.title ?? base.title
  const description = options?.description ?? base.description
  const canonical = absoluteUrl(base.path)
  const ogImage = base.ogImage ?? defaultOgImage
  const type = base.type ?? 'website'
  const htmlAttrs = { lang: base.lang ?? defaultLang }

  const meta = [
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: canonical },
    { property: 'og:site_name', content: siteName },
    { property: 'og:type', content: type },
    { property: 'og:image', content: ogImage },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: ogImage },
    { name: 'twitter:url', content: canonical },
  ]

  const scripts = (options?.jsonLd ?? []).map((schema) => ({
    type: 'application/ld+json',
    children: JSON.stringify(schema),
  }))

  return {
    title,
    htmlAttrs,
    meta,
    link: [{ rel: 'canonical', href: canonical }],
    script: scripts,
  }
}

export function getBreadcrumbJsonLd(page: PageKey) {
  const base = getPageMeta(page)
  const crumbs: Breadcrumb[] = base.breadcrumbs ?? [
    { name: 'Inicio', url: '/' },
    { name: base.title, url: base.path },
  ]

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.url),
    })),
  }
}

export function getMusicGroupJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'MusicGroup',
    name: siteName,
    url: siteUrl,
    genre: ['Banda de música', 'Música galega'],
    foundingLocation: 'Noia, Galicia, España',
    foundingDate: '1890',
    image: defaultOgImage,
    sameAs: [
      'https://www.facebook.com/bandademusicadenoia',
      'https://www.instagram.com/bandadenoia',
      'https://www.youtube.com/@bandademusicadenoia',
    ],
  }
}

export type EventLd = {
  name: string
  description: string
  startDate: string
  endDate?: string
  locationName: string
  locationAddress: string
  url?: string
}

export function getEventsJsonLd(events: EventLd[]) {
  return events.map((event) => ({
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.name,
    description: event.description,
    startDate: event.startDate,
    endDate: event.endDate ?? event.startDate,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: event.locationName,
      address: event.locationAddress,
    },
    organizer: {
      '@type': 'Organization',
      name: siteName,
      url: siteUrl,
    },
    url: absoluteUrl(event.url ?? '/events'),
    image: defaultOgImage,
  }))
}

export function useCanonicalFromRoute() {
  const route = useRoute()
  const path = route.fullPath || '/'
  return absoluteUrl(path)
}

